/**
 * Request timeout plumbing for the kitchen screen (feature 334, T041).
 *
 * `withTimeout` merges an optional caller signal (the scheduler's) with a
 * timeout, so one `fetch` is cut off by whichever fires first. It never throws
 * by itself: the caller reads `timedOut()` after an abort to tell a timeout
 * apart from a caller abort.
 */

/** Longest a full order list may take. */
export const FULL_LIST_TIMEOUT_MS = 30_000;
/** Longest the changes feed or the venue config may take. */
export const FEED_TIMEOUT_MS = 20_000;

export interface TimedSignal {
  signal: AbortSignal;
  timedOut(): boolean;
  /** Clears the timer and the listener on the caller signal. */
  dispose(): void;
}

export function withTimeout(timeoutMs: number, external?: AbortSignal): TimedSignal {
  const controller = new AbortController();
  let didTimeOut = false;
  const timer = setTimeout(() => {
    didTimeOut = true;
    controller.abort();
  }, timeoutMs);
  const onExternalAbort = () => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", onExternalAbort, { once: true });
  }
  return {
    signal: controller.signal,
    timedOut: () => didTimeOut,
    dispose() {
      clearTimeout(timer);
      external?.removeEventListener("abort", onExternalAbort);
    },
  };
}

/** An HTTP failure the sync loop can classify by status. */
export class HttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

/** The status of an HttpError, or undefined for anything else (network, abort). */
export function httpStatusOf(error: unknown): number | undefined {
  return error instanceof HttpError ? error.status : undefined;
}
